import { useState, useEffect } from 'react';

var API = 'https://api.algrok.com';

// ══════════════════════════════════════════════════════════════════════
//  API TOKEN INTERCEPTOR  (added 16-Aug-2026)
// ══════════════════════════════════════════════════════════════════════
// Until now this login gated only whether React rendered the dashboard.
// Every other fetch in the app was a bare fetch(API + '/...') with no
// credentials, so the API itself answered anyone — /amb2-state and /logs
// returned 200 over HTTPS to the whole internet, exposing strategy configs
// and 60 days of decision history.
//
// Rather than editing ~30 page files, window.fetch is wrapped once here.
// AuthGate mounts before anything else, so every existing call picks the
// token up automatically and no page needs to know this exists.
//
// A bearer token in the browser is not a secret from the user — it is a
// secret from everyone else, which is exactly the property needed. An API
// key would not work: it would ship inside the JS bundle and be readable
// in devtools by anyone who loaded the page.
// A 401 normally means the session expired, so the interceptor clears the token
// and reloads back to login. Some endpoints are fetched BEFORE login though —
// the nav asks /ui-config the moment it mounts — and treating those 401s as an
// expired session produces an infinite reload loop: mount -> 401 -> reload ->
// mount. Those endpoints must never trigger the reload.
function isPreAuthUrl(u) {
  return u.indexOf('/auth/') !== -1 || u.indexOf('/ui-config') !== -1;
}

if (typeof window !== 'undefined' && !window.__algrokFetchPatched) {
  window.__algrokFetchPatched = true;
  var _origFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var opts = init || {};
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isApi = url.indexOf(API) === 0;
    if (isApi) {
      var tok = null;
      try { tok = localStorage.getItem('algrok_token'); } catch (e) { tok = null; }
      if (tok) {
        opts = Object.assign({}, opts);
        opts.headers = Object.assign({}, opts.headers || {}, { 'X-Algrok-Token': tok });
      }
    }
    // ── SHARED GET LAYER ──────────────────────────────────────────
    // Single-flight + short TTL, so N callers asking for the SAME url at the
    // same moment produce ONE request. This is what lets the Workspace page
    // show nine panes without nine times the API load: every pane lives in
    // this one JavaScript context, so they can share. Separate browser TABS
    // cannot — each tab is an isolated context with its own timers.
    //
    // Deliberately narrow so it cannot change behaviour anywhere else:
    //   GET only · API base only · identical url only · POSTs never shared.
    // window.__algrokFetchTTL is the reuse window (default 900ms — long
    // enough to collapse simultaneous pollers, short enough that nothing
    // looks stale). The Workspace raises it to throttle every pane at once.
    var method = String((opts && opts.method) || 'GET').toUpperCase();
    // OPT-IN: only the Workspace needs this. It was built so nine panes in one
    // tab share requests; applying it to every page meant a 1s poller could be
    // served a body up to a TTL old, which is the wrong trade for a live MTM
    // readout. Off by default — Workspace sets the flag on mount and clears it
    // on unmount, so every other page fetches exactly as it did before.
    if (isApi && method === 'GET' && window.__algrokShareFetch) {
      var ttl = window.__algrokFetchTTL || 900;
      var now = Date.now();
      window.__algrokInflight = window.__algrokInflight || {};
      window.__algrokRecent = window.__algrokRecent || {};

      // Cache the BODY TEXT, never the Response object.
      //
      // The first version shared Response objects and handed out .clone()s. A
      // Response body can only be read once, so when a second caller joined an
      // in-flight request and cloned a response the first caller had already
      // consumed, clone() threw — the fetch rejected, the caller's .catch
      // swallowed it, and that strategy silently rendered as absent. On the
      // mobile dashboard that showed up as one tile blinking in and out every
      // second. Building a fresh Response per caller removes the whole class
      // of bug: nobody shares a body.
      var build = function (rec) {
        var noBody = rec.status === 204 || rec.status === 205 || rec.status === 304;
        return new Response(noBody ? null : rec.body, {
          status: rec.status,
          statusText: rec.statusText || '',
        });
      };

      var recent = window.__algrokRecent[url];
      if (recent && (now - recent.at) < ttl) {
        return Promise.resolve(build(recent));
      }

      var flight = window.__algrokInflight[url];
      if (flight) return flight.then(build);

      var p = _origFetch(input, opts).then(function (res) {
        delete window.__algrokInflight[url];
        if (res && res.status === 401 && !isPreAuthUrl(url)) {
          try {
            localStorage.removeItem('algrok_token');
            localStorage.removeItem('algrok_expires');
          } catch (e) { /* ignore */ }
          window.location.reload();
        }
        return res.text().then(function (body) {
          var rec = {
            at: Date.now(), body: body,
            status: res.status, statusText: res.statusText,
          };
          // Only successful responses are reusable. A 401 or 500 must not be
          // served to the next caller for the rest of the TTL.
          if (res.ok) window.__algrokRecent[url] = rec;
          return rec;
        });
      }).catch(function (e) {
        delete window.__algrokInflight[url];
        throw e;
      });
      window.__algrokInflight[url] = p;
      return p.then(build);
    }

    return _origFetch(input, opts).then(function (res) {
      // An expired or revoked session must send the user back to login rather
      // than leaving a dashboard that silently shows nothing. /auth/* is
      // excluded so a wrong PIN does not trigger a reload loop.
      if (res && res.status === 401 && isApi && !isPreAuthUrl(url)) {
        try {
          localStorage.removeItem('algrok_token');
          localStorage.removeItem('algrok_expires');
        } catch (e) { /* ignore */ }
        window.location.reload();
      }
      return res;
    });
  };
}

export default function AuthGate({ children }) {
  var [step, setStep] = useState('checking'); // checking, pin, otp, authenticated
  var [pin, setPin] = useState('');
  var [otp, setOtp] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);
  var [countdown, setCountdown] = useState(0);

  // Check existing session on mount
  useEffect(function() {
    var token = localStorage.getItem('algrok_token');
    var expires = parseInt(localStorage.getItem('algrok_expires') || '0');
    if (token && Date.now() < expires) {
      setStep('authenticated');
      // Tell anything that fetched before login to try again. The nav asks
      // /ui-config on mount, which happens BEFORE this component decides the
      // session is valid — without this it keeps whatever it had (on a fresh
      // device, nothing) until the next full page load.
      window.dispatchEvent(new Event('algrok-authenticated'));
    } else {
      localStorage.removeItem('algrok_token');
      localStorage.removeItem('algrok_expires');
      setStep('pin');
    }
  }, []);

  // OTP countdown
  useEffect(function() {
    if (step !== 'otp' || countdown <= 0) return;
    var id = setInterval(function() {
      setCountdown(function(c) {
        if (c <= 1) { clearInterval(id); return 0; }
        return c - 1;
      });
    }, 1000);
    return function() { clearInterval(id); };
  }, [step, countdown]);

  var submitPin = function() {
    if (pin.length !== 6) { setError('Enter 6-digit PIN'); return; }
    setLoading(true);
    setError('');
    fetch(API + '/auth/pin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin: pin }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.error) { setError(d.error); setLoading(false); return; }
      setStep('otp');
      setCountdown(90);
      setLoading(false);
    }).catch(function(e) {
      setError('Server unreachable');
      setLoading(false);
    });
  };

  var submitOtp = function() {
    if (otp.length !== 6) { setError('Enter 6-digit OTP'); return; }
    setLoading(true);
    setError('');
    fetch(API + '/auth/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ otp: otp }),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.error) { setError(d.error); setLoading(false); return; }
      localStorage.setItem('algrok_token', d.token);
      localStorage.setItem('algrok_expires', String(d.expires));
      setStep('authenticated');
      window.dispatchEvent(new Event('algrok-authenticated'));
      setLoading(false);
    }).catch(function(e) {
      setError('Server unreachable');
      setLoading(false);
    });
  };

  var handlePinKey = function(e) { if (e.key === 'Enter') submitPin(); };
  var handleOtpKey = function(e) { if (e.key === 'Enter') submitOtp(); };

  if (step === 'checking') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center' }}>
            <img src="/algrok-logo.jpg" alt="Algrok" style={{ width: 64, height: 64, borderRadius: 12, marginBottom: 8 }} />
            <div style={{ fontSize: 12, color: '#556677', marginTop: 8 }}>Verifying session...</div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'authenticated') {
    return children;
  }

  return (
    <div style={shell}>
      <div style={card}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/algrok-logo.jpg" alt="Algrok" style={{ width: 80, height: 80, borderRadius: 16, marginBottom: 8 }} />
          <div style={{ fontSize: 11, color: '#556677' }}>Algo Trading</div>
        </div>

        {step === 'pin' && (
          <div>
            <div style={{ fontSize: 12, color: '#8899aa', marginBottom: 8, textAlign: 'center' }}>Enter your 6-digit PIN</div>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={function(e) { setPin(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
              onKeyDown={handlePinKey}
              autoFocus
              style={inputStyle}
              placeholder="• • • • • •"
            />
            <button onClick={submitPin} disabled={loading || pin.length !== 6} style={Object.assign({}, btnStyle, { opacity: loading || pin.length !== 6 ? 0.5 : 1 })}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
          </div>
        )}

        {step === 'otp' && (
          <div>
            <div style={{ fontSize: 12, color: '#00d4aa', marginBottom: 4, textAlign: 'center' }}>{'\u2713'} PIN verified</div>
            <div style={{ fontSize: 11, color: '#8899aa', marginBottom: 8, textAlign: 'center' }}>
              OTP sent to Telegram
              {countdown > 0 && <span style={{ color: '#fbbf24', marginLeft: 6 }}>{countdown}s</span>}
              {countdown <= 0 && <span style={{ color: '#ff4466', marginLeft: 6 }}>Expired</span>}
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={function(e) { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
              onKeyDown={handleOtpKey}
              autoFocus
              style={inputStyle}
              placeholder="Enter OTP"
            />
            <button onClick={submitOtp} disabled={loading || otp.length !== 6 || countdown <= 0} style={Object.assign({}, btnStyle, { opacity: loading || otp.length !== 6 || countdown <= 0 ? 0.5 : 1 })}>
              {loading ? 'Verifying...' : 'Login'}
            </button>
            {countdown <= 0 && (
              <button onClick={function() { setStep('pin'); setPin(''); setOtp(''); setError(''); }} style={Object.assign({}, btnStyle, { background: 'transparent', color: '#8899aa', border: '1px solid rgba(100,100,120,0.3)', marginTop: 8 })}>
                Retry
              </button>
            )}
          </div>
        )}

        {error && <div style={{ color: '#ff4466', fontSize: 11, textAlign: 'center', marginTop: 10 }}>{error}</div>}
      </div>
    </div>
  );
}

var shell = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: '100vh', width: '100vw',
  background: '#080e1d',
  fontFamily: "'Inter', sans-serif",
};

var card = {
  width: 320, padding: '32px 28px', borderRadius: 12,
  background: '#0d1526',
  border: '1px solid rgba(26,45,74,0.6)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

var inputStyle = {
  width: '100%', padding: '12px 16px', fontSize: 24, fontWeight: 700,
  textAlign: 'center', letterSpacing: 8,
  background: '#0a0f1e', color: '#e2e8f0',
  border: '2px solid rgba(14,165,233,0.3)', borderRadius: 8,
  outline: 'none', fontFamily: "'JetBrains Mono', monospace",
  boxSizing: 'border-box',
};

var btnStyle = {
  width: '100%', padding: '12px', marginTop: 12,
  borderRadius: 8, fontSize: 13, fontWeight: 700,
  cursor: 'pointer', border: 'none',
  fontFamily: "'Inter', sans-serif",
  background: 'linear-gradient(135deg, #0ea5e9, #00d4aa)',
  color: '#fff',
};
